import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import LoadingState from "../Common/LoadingState";
import AuthNavbar from "./AuthNavbar";
import {
  requestSignupOTP,
  verifySignupOTP,
  resendSignupOTP,
} from "../../services/auth";
import {
  validateEmail,
  validatePhone,
  formatPhone,
  validateIndianPhone,
  formatIndianPhone,
  isTemporaryEmail,
  isFreeEmail,
  hasEmailPlusAddressing,
} from "../../utils/validation";
import PhoneInput from "../Common/PhoneInput";
import "./Auth.css";
import { useTheme } from "../../contexts/ThemeContext";

declare global {
  interface Window {
    turnstile: any;
    onloadTurnstileCallback?: () => void;
  }
}

// Environment check
const IS_DEVELOPMENT = process.env.NODE_ENV === "development";

const TURNSTILE_SITE_KEY =
  process.env.REACT_APP_TURNSTILE_SITE_KEY || "1x00000000000000000000XX";

enum SignupStep {
  FORM,
  OTP_VERIFICATION,
  SUCCESS,
}

// Add back button component
const BackButton: React.FC = () => {
  return (
    <Link
      to="https://ukti.ai"
      className="flex items-center text-sm text-[color:var(--muted-foreground)] hover:text-[color:var(--color-secondary)] transition-colors mb-4"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="mr-1"
      >
        <path d="M19 12H5M12 19l-7-7 7-7" />
      </svg>
      Back to Home
    </Link>
  );
};

interface SignupProps {
  onLoginSuccess: () => void;
}

const Signup: React.FC<SignupProps> = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const { theme } = useTheme(); // Get current theme from context
  const [step, setStep] = useState<SignupStep>(SignupStep.FORM);
  const [fullname, setFullname] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [company, setCompany] = useState<string>("");
  const [otpCode, setOtpCode] = useState<string>("");
  const [platformOtp, setPlatformOtp] = useState<string>("");
  const [maxUses, setMaxUses] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [tryCount, setTryCount] = useState<number>(0);
  const [emailValidationMsg, setEmailValidationMsg] = useState<string | null>(
    null
  );
  const [phoneValidationMsg, setPhoneValidationMsg] = useState<string | null>(
    null
  );
  const [fullnameValidationMsg, setFullnameValidationMsg] = useState<
    string | null
  >(null);
  const [companyValidationMsg, setCompanyValidationMsg] = useState<
    string | null
  >(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [showResendModal, setShowResendModal] = useState<boolean>(false);
  const turnstileRef = useRef<any>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const turnstileContainer = useRef<HTMLDivElement>(null);
  const turnstileScriptLoaded = useRef<boolean>(false);

  // Create separate turnstile references for the resend modal
  const modalTurnstileRef = useRef<any>(null);
  const modalTurnstileWidgetId = useRef<string | null>(null);
  const modalTurnstileContainer = useRef<HTMLDivElement>(null);

  // Load Turnstile script only once when component mounts
  useEffect(() => {
    // Only proceed if we're on the form step and script hasn't been loaded yet
    // Skip loading Turnstile in development
    if (
      IS_DEVELOPMENT ||
      step !== SignupStep.FORM ||
      turnstileScriptLoaded.current
    )
      return;

    // Mark script as loaded to prevent duplicate loading
    turnstileScriptLoaded.current = true;

    // Create the script element
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback";
    script.async = true;
    script.defer = true;
    script.id = "turnstile-script";

    // Set up the callback
    window.onloadTurnstileCallback = () => {
      initializeTurnstile();
    };

    // Add script to body
    document.body.appendChild(script);

    // Cleanup function
    return () => {
      if (document.getElementById("turnstile-script")) {
        document.body.removeChild(script);
      }
      // Clean up the callback when unmounting
      if (window.onloadTurnstileCallback) {
        delete window.onloadTurnstileCallback;
      }
      // Remove widget if it exists
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
      }
    };
  }, [step]);

  // Add a function to initialize or reinitialize Turnstile
  const initializeTurnstile = () => {
    if (turnstileContainer.current && window.turnstile) {
      // If a widget already exists, remove it first
      if (turnstileWidgetId.current) {
        window.turnstile.remove(turnstileWidgetId.current);
      }

      // Create new widget
      turnstileWidgetId.current = window.turnstile.render(
        turnstileContainer.current,
        {
          sitekey: TURNSTILE_SITE_KEY,
          theme: theme === "light" ? "light" : "dark",
          callback: (token: string) => {
            turnstileRef.current = token;
          },
          "error-callback": () => {
            turnstileRef.current = null;
          },
        }
      );
    }
  };

  // Effect to handle theme changes
  useEffect(() => {
    if (!IS_DEVELOPMENT && turnstileScriptLoaded.current && window.turnstile) {
      initializeTurnstile();
    }
  }, [theme]);

  // Reset Turnstile when coming back to the form step or when showResendModal changes
  useEffect(() => {
    if (
      !IS_DEVELOPMENT &&
      turnstileWidgetId.current &&
      window.turnstile &&
      (step === SignupStep.FORM || showResendModal)
    ) {
      try {
        window.turnstile.reset(turnstileWidgetId.current);
      } catch (error) {
        console.warn("Failed to reset Turnstile widget:", error);
        // Re-initialize the widget if reset fails
        if (step === SignupStep.FORM) {
          setTimeout(() => {
            initializeTurnstile();
          }, 100);
        }
      }
    }
  }, [step, showResendModal]);

  // Effect to initialize or reset Turnstile in the modal when it opens
  useEffect(() => {
    if (!IS_DEVELOPMENT && showResendModal && window.turnstile) {
      // Small delay to ensure the container is mounted in the DOM
      const timer = setTimeout(() => {
        if (modalTurnstileContainer.current) {
          // If a widget already exists, remove it first
          if (modalTurnstileWidgetId.current) {
            window.turnstile.remove(modalTurnstileWidgetId.current);
          }

          // Create new widget
          modalTurnstileWidgetId.current = window.turnstile.render(
            modalTurnstileContainer.current,
            {
              sitekey: TURNSTILE_SITE_KEY,
              theme: theme === "light" ? "light" : "dark",
              callback: (token: string) => {
                modalTurnstileRef.current = token;
              },
              "error-callback": () => {
                modalTurnstileRef.current = null;
              },
            }
          );
        }
      }, 100);

      return () => clearTimeout(timer);
    }

    // Cleanup the widget when the modal closes
    return () => {
      if (
        !IS_DEVELOPMENT &&
        modalTurnstileWidgetId.current &&
        window.turnstile
      ) {
        window.turnstile.remove(modalTurnstileWidgetId.current);
        modalTurnstileWidgetId.current = null;
      }
    };
  }, [showResendModal, theme]);

  // Countdown timer effect for OTP resend
  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setTimeout(() => {
      setTimeLeft((time) => time - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft]);

  // Add effect to automatically copy OTP when success screen is displayed
  useEffect(() => {
    if (step === SignupStep.SUCCESS && platformOtp) {
      copyToClipboard();
    }
  }, [step, platformOtp]);

  // Handle signup form submission
  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      // Get Turnstile token - skip verification in development mode
      const turnstileToken = IS_DEVELOPMENT
        ? "dev_verification_token"
        : turnstileRef.current;

      if (!turnstileToken) {
        setError("Please complete the security check");
        setLoading(false);
        return;
      }

      // Format phone number to E.164 format
      const formattedPhone = formatPhone(phone);

      // Submit signup request
      const result = await requestSignupOTP({
        fullname: fullname.trim(),
        email: email.trim(),
        phone: formattedPhone,
        company: company.trim(),
        turnstileToken,
      });

      if (result.success) {
        setSuccess("OTP sent successfully to your phone");
        setTimeLeft(60); // Set 1 minute countdown for resend
        setStep(SignupStep.OTP_VERIFICATION);
      } else {
        setError(result.error || "Failed to send OTP. Please try again.");
        // Reset the Turnstile widget
        if (!IS_DEVELOPMENT && turnstileWidgetId.current && window.turnstile) {
          window.turnstile.reset(turnstileWidgetId.current);
        }
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error(err);
      // Reset the Turnstile widget
      if (!IS_DEVELOPMENT && turnstileWidgetId.current && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId.current);
      }
    } finally {
      setLoading(false);
    }
  };

  // Validate the signup form
  const validateForm = (): boolean => {
    if (!fullname.trim()) {
      setError("Please enter your full name");
      return false;
    }

    if (!email.trim() || !validateEmail(email)) {
      setError("Please enter a valid email address");
      return false;
    }

    if (isTemporaryEmail(email)) {
      setError("Temporary or disposable email addresses are not allowed");
      return false;
    }

    if (isFreeEmail(email)) {
      setError(
        "Personal email addresses are not allowed. Please use a work email"
      );
      return false;
    }

    if (hasEmailPlusAddressing(email)) {
      setError("Email addresses with '+' are not allowed");
      return false;
    }

    if (!phone.trim() || !validatePhone(phone)) {
      setError("Please enter a valid phone number");
      return false;
    }

    if (!company.trim()) {
      setError("Please enter your company name");
      return false;
    }

    return true;
  };

  // Validate email on blur
  const validateEmailOnBlur = () => {
    setEmailValidationMsg(null);

    if (!email.trim()) {
      setEmailValidationMsg("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setEmailValidationMsg("Please enter a valid email address");
      return;
    }

    if (isTemporaryEmail(email)) {
      setEmailValidationMsg(
        "Temporary or disposable email addresses are not allowed"
      );
      return;
    }

    if (isFreeEmail(email)) {
      setEmailValidationMsg(
        "Personal email addresses are not allowed. Please use a work email"
      );
      return;
    }

    if (hasEmailPlusAddressing(email)) {
      setEmailValidationMsg("Email addresses with '+' are not allowed");
      return;
    }
  };

  // Validate phone on blur
  const validatePhoneOnBlur = () => {
    setPhoneValidationMsg(null);

    if (!phone.trim()) {
      setPhoneValidationMsg("Phone number is required");
      return;
    }

    if (!validatePhone(phone)) {
      setPhoneValidationMsg("Please enter a valid phone number");
    }
  };

  // Validate fullname on blur
  const validateFullnameOnBlur = () => {
    setFullnameValidationMsg(null);

    if (!fullname.trim()) {
      setFullnameValidationMsg("Full name is required");
    }
  };

  // Validate company on blur
  const validateCompanyOnBlur = () => {
    setCompanyValidationMsg(null);

    if (!company.trim()) {
      setCompanyValidationMsg("Company/Organization is required");
    }
  };

  // Prepare for resend OTP by showing the modal
  const prepareResendOTP = () => {
    // Only proceed if the timer is not running
    if (timeLeft > 0) return;

    // Show the modal
    setShowResendModal(true);
    setError(null);
  };

  // Handle OTP resend - use modal turnstile ref
  const handleResendOTP = async () => {
    if (timeLeft > 0) return;

    // Get Turnstile token - skip verification in development mode
    const turnstileToken = IS_DEVELOPMENT
      ? "dev_verification_token"
      : modalTurnstileRef.current;

    if (!IS_DEVELOPMENT && !turnstileToken) {
      setError("Please complete the security check");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formattedPhone = formatPhone(phone);
      const result = await resendSignupOTP(formattedPhone, turnstileToken);

      if (result.success) {
        setSuccess("OTP has been resent to your phone");
        setTimeLeft(60); // Reset countdown
        setShowResendModal(false); // Hide the modal after successful resend

        // Reset the modal turnstile reference
        modalTurnstileRef.current = null;
      } else {
        setError(result.error || "Failed to resend OTP. Please try again.");
        // Reset the Turnstile widget if not in development mode
        if (
          !IS_DEVELOPMENT &&
          modalTurnstileWidgetId.current &&
          window.turnstile
        ) {
          window.turnstile.reset(modalTurnstileWidgetId.current);
        }
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error(err);
      // Reset the Turnstile widget if not in development mode
      if (
        !IS_DEVELOPMENT &&
        modalTurnstileWidgetId.current &&
        window.turnstile
      ) {
        window.turnstile.reset(modalTurnstileWidgetId.current);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP verification
  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otpCode.trim()) {
      setError("Please enter the OTP sent to your phone");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formattedPhone = formatPhone(phone);
      const result = await verifySignupOTP(formattedPhone, otpCode);

      if (result.success) {
        // If we received a JWT token in the response, proceed to dashboard or pending agent
        if (result.access_token) {
          // Stop showing loading indicator to show success animation
          setLoading(false);
          setSuccess("Signup successful! Redirecting...");

          // Short delay to show success message before redirecting
          setTimeout(() => {
            // Direct redirect through parent component
            onLoginSuccess();
          }, 600);
        } else {
          setError(
            "Authentication failed. Please try again or contact support."
          );
          setLoading(false);
        }
      } else {
        setTryCount((count) => count + 1);
        if (tryCount >= 2) {
          setError(
            "Maximum verification attempts reached. Please request a new OTP."
          );
        } else {
          setError(result.error || "Invalid OTP. Please try again.");
        }
        setLoading(false);
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error(err);
      setLoading(false);
    }
  };

  // Update the copy to clipboard function to use window.navigator for better compatibility
  const copyToClipboard = () => {
    navigator.clipboard.writeText(platformOtp).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      (err) => {
        console.error("Could not copy text: ", err);
      }
    );
  };

  // Add a safe navigation function to handle going back to the form
  const safeNavigateToForm = () => {
    // First set the step to avoid any race conditions
    setStep(SignupStep.FORM);

    // If we're in production, try to reinitialize Turnstile after a short delay
    if (!IS_DEVELOPMENT && window.turnstile) {
      setTimeout(() => {
        try {
          initializeTurnstile();
        } catch (error) {
          console.warn("Failed to initialize Turnstile widget:", error);
        }
      }, 300);
    }
  };

  // Render appropriate step UI
  const renderStep = () => {
    switch (step) {
      case SignupStep.FORM:
        return (
          <>
            <AuthNavbar />
            <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-8 px-4 sm:px-6 lg:px-8 relative pt-20">
              <motion.div
                className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                <div className="flex flex-col items-center mb-6">
                  <motion.h2
                    className="text-3xl pt-2 font-bold text-[color:var(--foreground)]"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    Create Account
                  </motion.h2>
                  <motion.p
                    className="text-sm text-[color:var(--muted-foreground)] mt-1 text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    Fill out this form to create your Voice Agent Portal account
                  </motion.p>
                </div>
                <motion.form
                  onSubmit={handleSignupSubmit}
                  className="space-y-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    <label
                      htmlFor="fullname"
                      className="block text-sm font-medium text-[color:var(--foreground)]"
                    >
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="fullname"
                      value={fullname}
                      onChange={(e) => setFullname(e.target.value)}
                      onBlur={validateFullnameOnBlur}
                      placeholder="Enter your full name"
                      autoComplete="name"
                      disabled={loading}
                      className="mt-1 block w-full px-3 py-2 bg-transparent border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-secondary)] focus:border-transparent text-[color:var(--foreground)] [&:-webkit-autofill]:text-[color:var(--foreground)] [&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:!transition-colors"
                    />
                    {fullnameValidationMsg && (
                      <motion.p
                        className="mt-1 text-sm text-red-600"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        {fullnameValidationMsg}
                      </motion.p>
                    )}
                  </motion.div>

                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                  >
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-[color:var(--foreground)]"
                    >
                      Work Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onBlur={validateEmailOnBlur}
                      placeholder="Enter your work email"
                      autoComplete="email"
                      disabled={loading}
                      className="mt-1 block w-full px-3 py-2 bg-transparent border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-secondary)] focus:border-transparent text-[color:var(--foreground)] [&:-webkit-autofill]:text-[color:var(--foreground)] [&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:!transition-colors"
                    />
                    {emailValidationMsg && (
                      <motion.p
                        className="mt-1 text-sm text-red-600"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        {emailValidationMsg}
                      </motion.p>
                    )}
                  </motion.div>

                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                  >
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-[color:var(--foreground)]"
                    >
                      Phone Number
                    </label>
                    <PhoneInput
                      id="phone"
                      value={phone}
                      onChange={(value) => setPhone(value || "")}
                      onBlur={validatePhoneOnBlur}
                      placeholder="Enter your phone number"
                      autoComplete="tel"
                      disabled={loading}
                      className="mt-1 block w-full"
                      error={!!phoneValidationMsg}
                    />
                    {phoneValidationMsg && (
                      <motion.p
                        className="mt-1 text-sm text-red-600"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        {phoneValidationMsg}
                      </motion.p>
                    )}
                  </motion.div>

                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                  >
                    <label
                      htmlFor="company"
                      className="block text-sm font-medium text-[color:var(--foreground)]"
                    >
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      onBlur={validateCompanyOnBlur}
                      placeholder="Enter your company name"
                      autoComplete="organization"
                      disabled={loading}
                      className="mt-1 block w-full px-3 py-2 bg-transparent border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-secondary)] focus:border-transparent text-[color:var(--foreground)] [&:-webkit-autofill]:text-[color:var(--foreground)] [&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:!transition-colors"
                    />
                    {companyValidationMsg && (
                      <motion.p
                        className="mt-1 text-sm text-red-600"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        {companyValidationMsg}
                      </motion.p>
                    )}
                  </motion.div>

                  {/* Turnstile Widget - Only show in production */}
                  {!IS_DEVELOPMENT ? (
                    <div className="flex justify-center">
                      <div ref={turnstileContainer}></div>
                    </div>
                  ) : (
                    <div className="p-3 bg-blue-50 border border-blue-200 text-blue-600 rounded-md text-sm">
                      Development mode: Turnstile verification bypassed
                    </div>
                  )}

                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm">
                      {error}
                    </div>
                  )}

                  {success && (
                    <motion.div
                      className="p-3 bg-green-50 border border-green-200 rounded-md text-sm"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0, scale: [1, 1.03, 1] }}
                      transition={{
                        duration: 0.4,
                        scale: { duration: 0.5, times: [0, 0.5, 1] },
                      }}
                    >
                      <div className="flex items-center">
                        <div className="shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                          <motion.svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-green-600"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 0.4, delay: 0.2 }}
                          >
                            <path d="M20 6L9 17l-5-5"></path>
                          </motion.svg>
                        </div>
                        <span className="text-green-600 font-medium">
                          {success}
                        </span>
                      </div>
                    </motion.div>
                  )}

                  <motion.button
                    type="submit"
                    disabled={loading}
                    className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {loading ? "Creating..." : "Create Account"}
                  </motion.button>
                </motion.form>
                <motion.div
                  className="mt-6 text-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <Link
                    to="/login"
                    className="text-sm text-[color:var(--foreground)] hover:text-[color:var(--color-secondary)] font-medium"
                  >
                    Already have an account? Login here
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </>
        );

      case SignupStep.OTP_VERIFICATION:
        return (
          <>
            <AuthNavbar />
            <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-12 px-4 sm:px-6 lg:px-8 relative pt-20">
              <div className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8">
                {/* Back to Signup button at the top right */}
                <div className="flex justify-start mb-4">
                  <button
                    onClick={safeNavigateToForm}
                    className="flex items-center text-sm text-[color:var(--foreground)] hover:text-[color:var(--color-secondary)] transition-colors px-3 py-1.5 rounded-full border border-[color:var(--border)] bg-[color:var(--card)] hover:bg-[color:var(--muted)]/10"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-1"
                    >
                      <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    Back to Signup
                  </button>
                </div>

                <div className="flex flex-col items-center mb-8">
                  <motion.h2
                    className="text-3xl pt-2 font-bold text-[color:var(--foreground)]"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    Verify Your Phone
                  </motion.h2>
                  <motion.p
                    className="text-sm text-[color:var(--muted-foreground)] mt-1 text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    We've sent a verification code to your phone. Please enter
                    it below.
                  </motion.p>
                </div>

                <motion.form
                  onSubmit={handleVerifyOTP}
                  className="space-y-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <div>
                    <label
                      htmlFor="otp"
                      className="block text-sm font-medium text-[color:var(--foreground)]"
                    >
                      Verification Code
                    </label>
                    <input
                      type="text"
                      id="otp"
                      value={otpCode}
                      onChange={(e) => {
                        // Filter out non-numeric characters
                        const numericValue = e.target.value.replace(/\D/g, "");
                        setOtpCode(numericValue);
                      }}
                      placeholder="Enter the verification code"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="one-time-code"
                      disabled={loading}
                      className="mt-1 block w-full px-3 py-2 bg-transparent border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-secondary)] focus:border-transparent text-[color:var(--foreground)] [&:-webkit-autofill]:text-[color:var(--foreground)] [&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:!transition-colors"
                    />
                  </div>

                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm">
                      {error}
                    </div>
                  )}

                  {success && (
                    <motion.div
                      className="p-3 bg-green-50 border border-green-200 rounded-md text-sm"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0, scale: [1, 1.03, 1] }}
                      transition={{
                        duration: 0.4,
                        scale: { duration: 0.5, times: [0, 0.5, 1] },
                      }}
                    >
                      <div className="flex items-center">
                        <div className="shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                          <motion.svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-green-600"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 0.4, delay: 0.2 }}
                          >
                            <path d="M20 6L9 17l-5-5"></path>
                          </motion.svg>
                        </div>
                        <span className="text-green-600 font-medium">
                          {success}
                        </span>
                      </div>
                    </motion.div>
                  )}

                  <motion.button
                    type="submit"
                    disabled={loading}
                    className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {loading ? "Verifying..." : "Verify"}
                  </motion.button>

                  <motion.div
                    className="mt-4 text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    <p className="text-sm font-semibold text-[color:var(--muted-foreground)]">
                      Didn't receive the code?{" "}
                      {timeLeft > 0 ? (
                        <span>Resend in {timeLeft}s</span>
                      ) : (
                        <motion.button
                          type="button"
                          onClick={prepareResendOTP}
                          disabled={loading}
                          className="text-[color:var(--color-secondary)] hover:underline font-medium"
                          whileHover={{ scale: 1.05 }}
                        >
                          Resend Code
                        </motion.button>
                      )}
                    </p>
                  </motion.div>

                  {/* Change phone number link */}
                  <motion.div
                    className="!mt-2 text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                  >
                    <button
                      onClick={safeNavigateToForm}
                      className="text-sm text-[color:var(--foreground)] hover:text-[color:var(--color-secondary)] font-medium"
                    >
                      Change phone number?
                    </button>
                  </motion.div>
                </motion.form>
              </div>
            </div>
          </>
        );

      case SignupStep.SUCCESS:
        return (
          <>
            <AuthNavbar />
            <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-12 px-4 sm:px-6 lg:px-8 relative pt-20">
              <div className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8">
                <div className="flex flex-col items-center mb-8">
                  <motion.div
                    className="flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 15,
                      duration: 0.6,
                    }}
                  >
                    <svg
                      className="h-8 w-8 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </motion.div>
                  <motion.h2
                    className="text-3xl pt-2 font-bold text-[color:var(--foreground)]"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    Registration Successful!
                  </motion.h2>
                  <motion.p
                    className="text-sm text-[color:var(--muted-foreground)] mt-2 text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    Your platform one-time password is:
                  </motion.p>
                  <motion.div
                    className="my-3 px-6 py-2 bg-accent/10 rounded-md border border-accent/20 flex items-center gap-2 relative"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    <span className="font-mono font-bold text-xl text-accent">
                      {platformOtp}
                    </span>
                    {copied && (
                      <motion.div
                        className="absolute inset-0 flex items-center justify-center rounded-md bg-accent/90 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <span className="text-accent font-medium flex items-center gap-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-accent"
                          >
                            <path d="M20 6L9 17l-5-5"></path>
                          </svg>
                          Copied
                        </span>
                      </motion.div>
                    )}
                    <motion.button
                      type="button"
                      onClick={copyToClipboard}
                      className="ml-auto p-1.5 rounded-md hover:bg-accent/20 transition-colors text-accent"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      title="Copy to clipboard"
                    >
                      {copied ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 6L9 17l-5-5"></path>
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect
                            x="9"
                            y="9"
                            width="13"
                            height="13"
                            rx="2"
                            ry="2"
                          ></rect>
                          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
                        </svg>
                      )}
                    </motion.button>
                  </motion.div>

                  <motion.p
                    className="text-sm text-[color:var(--muted-foreground)] mt-2 text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                  >
                    Please save this password as you will need it to login. This
                    password can be used{" "}
                    <strong className="font-bold">{maxUses}</strong> time(s).
                  </motion.p>
                </div>

                <motion.button
                  onClick={() => navigate("/login")}
                  className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)]"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Go to Login
                </motion.button>
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {renderStep()}

      {/* Resend OTP Modal */}
      <AnimatePresence>
        {showResendModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowResendModal(false)}
          >
            <motion.div
              className="bg-[color:var(--card)] rounded-lg shadow-xl max-w-md w-full p-6"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-[color:var(--foreground)]">
                  Resend Verification Code
                </h3>
                <button
                  onClick={() => setShowResendModal(false)}
                  className="text-[color:var(--muted-foreground)] hover:text-[color:var(--foreground)] transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              <p className="text-sm text-[color:var(--muted-foreground)] mb-4">
                Please complete the security check to receive a new verification
                code.
              </p>

              {/* Turnstile Widget - using modal-specific container */}
              {!IS_DEVELOPMENT ? (
                <div className="flex justify-center mb-4">
                  <div ref={modalTurnstileContainer}></div>
                </div>
              ) : (
                <div className="p-3 bg-blue-50 border border-blue-200 text-blue-600 rounded-md text-sm mb-4">
                  Development mode: Turnstile verification bypassed
                </div>
              )}

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm mb-4">
                  {error}
                </div>
              )}

              <div className="flex space-x-3">
                <motion.button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={
                    loading || (!IS_DEVELOPMENT && !modalTurnstileRef.current)
                  }
                  className="flex-1 py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? "Sending..." : "Resend Code"}
                </motion.button>

                <motion.button
                  type="button"
                  onClick={() => setShowResendModal(false)}
                  className="py-2 px-4 border border-[color:var(--border)] text-[color:var(--foreground)] rounded-md text-sm hover:bg-[color:var(--muted)]/10 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-10">
          <LoadingState
            size={
              step === SignupStep.OTP_VERIFICATION && success
                ? "large"
                : "medium"
            }
            padding="none"
            message={
              step === SignupStep.OTP_VERIFICATION
                ? success
                  ? "Redirecting..."
                  : "Verifying..."
                : "Sending OTP..."
            }
            fullPage={false}
          />
        </div>
      )}
    </>
  );
};

export default Signup;
